import {
  Component,
  ContentChild,
  EventEmitter,
  forwardRef,
  Input,
  OnInit,
  Output,
  TemplateRef,
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  FormBuilder,
  FormControl,
  FormGroup,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator,
  Validators,
} from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { Country } from 'apps/app-cic-ciam/src/app/shared/country.model';
import { SupportService } from 'apps/app-cic-ciam/src/services/support.service';
import { ToastrService } from 'ngx-toastr';

type Nullable<T> = T | undefined | null;

interface CountryDropdownForm {
  country: Nullable<Country>;
  phone: string | null;
}

@Component({
  selector: 'ciciam-country-dropdown',
  templateUrl: './country-dropdown.component.html',
  styleUrls: ['./country-dropdown.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CountryDropdownComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => CountryDropdownComponent),
      multi: true,
    },
  ],
})
export class CountryDropdownComponent
  implements OnInit, ControlValueAccessor, Validator
{
  @Input() enableCountry: boolean = true;
  @Input() isSubmitting: boolean = false;
  @Input() disabled: boolean = true;
  @Input() errorMessage?: string;
  @Output() formSubmitted = new EventEmitter<string>();
  @ContentChild('dropdownMenuItem') dropdownMenuItem!: TemplateRef<any>;
  @ContentChild('submitButton') submitButton!: TemplateRef<any>;
  @ContentChild('verifiedStatus') verifiedStatus!: TemplateRef<any>;

  protected onTouched!: () => void;

  buttonClasses = {
    regular: {
      enabled: [
        'text-white',
        'bg-notus-primary-600',
        'hover:bg-notus-primary-700',
        'focus:ring-notus-primary-300',
        'dark:bg-primary-600',
        'dark:hover:bg-primary-700',
        'dark:focus:ring-primary-800',
      ],
      disabled: [
        'bg-notus-primary-300',
        'dark:bg-primary-500',
        'cursor-not-allowed',
      ],
    },
    loading: ['bg-white', 'ring-notus-primary-700', 'ring-1', 'cursor-default'],
    dropdown: {
      enabled: [
        'bg-white',
        'text-notus-primary-700',
        'ring-1',
        'focus:ring-1',
        'ring-notus-primary-700',
      ],
      disabled: [
        'bg-white',
        'text-notus-primary-700',
        'ring-1',
        'focus:ring-1',
        'ring-notus-primary-700',
        'cursor-not-allowed',
      ],
    },
  };

  form = this.formBuilder.group<{
    country?: FormControl<Nullable<Country>>;
    phone: FormControl<string | null>;
  }>(
    {
      phone: new FormControl('', [Validators.required]),
    },
    {
      validators: [
        (group: AbstractControl) => {
          const _group = group as FormGroup;
          const errors = Object.keys(_group.controls)
            .map((key) => {
              const control = _group.get(key);

              const errors = control?.validator?.(control) ?? null;
              control?.setErrors(errors);
              if (control) {
                // console.log(
                //   `validators; ${key} - setErrors: ${JSON.stringify(errors)}`
                // );
              }

              return errors;
            })
            .filter((errors) => !!errors);

          // console.log(
          //   `validators; aggregate errors: ${JSON.stringify(errors)}`
          // );

          return errors;
        },
      ],
    }
  );

  isCountryListLoading: boolean = false;

  private _countryList: Country[] = [];

  private mapControls<T>(
    callbackFn: (key: string, control: AbstractControl) => T
  ): T[] {
    const _group = this.form as FormGroup;

    return Object.keys(_group.controls).map((key) => {
      const control = _group.get(key);

      return callbackFn(key, control!);
    });
  }

  get isPhoneValid(): boolean {
    return !this.form.controls.phone.errors;
  }

  get suffixButtonClasses(): string[] {
    let _classes: string[] = [];

    if (this.isSubmitting) {
      _classes = _classes.concat(this.buttonClasses.loading);
    } else if (this.disabled || !this.isFormValid()) {
      _classes = _classes.concat(this.buttonClasses.regular.disabled);
    }

    if (this.verifiedStatus) {
      _classes = _classes.concat(['bg-white']);
    }

    return _classes.length ? _classes : this.buttonClasses.regular.enabled;
  }

  isFormValid(pick?: string | string[]): boolean {
    const _group = this.form as FormGroup;

    const errors = Object.keys(_group.controls)
      .filter((key) => {
        if (Array.isArray(pick) && pick.length) {
          return pick.includes(key);
        } else if (pick) {
          return key == pick;
        }

        return true;
      })
      .map((key) => {
        const control = _group.get(key);

        return !control!.errors;
      });

    return errors.every((valid) => valid);
  }

  get countryIddRegex(): RegExp {
    const regexString = this._countryList
      .filter((country) => country.idd !== '')
      .map((country) => `^\\${country.idd}`)
      .join('|');

    return new RegExp(regexString);
  }

  get countryList(): Country[] {
    return this._countryList;
  }

  set countryList(value) {
    this._countryList = value;
  }

  get formValues(): Partial<CountryDropdownForm> {
    return this.form.getRawValue();
  }

  constructor(
    private formBuilder: FormBuilder,
    private toastr: ToastrService,
    public translate: TranslateService,
    private supportService: SupportService
  ) {}

  ngOnInit(): void {
    if (this.enableCountry) {
      this.fetchCountryList();
    }

    // Strip non-digit characters from phone input as the user types
    this.form.controls.phone.valueChanges.subscribe((value) => {
      if (value) {
        const cleaned = value.replace(/\D/g, '');
        if (cleaned !== value) {
          this.form.controls.phone.setValue(cleaned, { emitEvent: false });
        }
      }
    });
  }

  writeValue(obj: any): void {
    console.log(`writeValue; obj: ${JSON.stringify(obj)}`);

    if (typeof obj === 'string') {
      // Normalize: ensure the value has a leading '+' for IDD matching
      const normalized = obj.startsWith('+') ? obj : `+${obj}`;
      const idd = normalized.match(this.countryIddRegex)?.toString() ?? '';

      if (idd) {
        if (this.form.controls.country) {
          obj = normalized.replace(idd, '');

          const country = this._countryList.find(
            (country) => country.idd === idd
          );

          if (country) {
            this.form.controls.country.setValue(country, { emitEvent: false });
          }
        }
      }

      this.form.controls.phone.setValue(obj, { emitEvent: false });
    }
  }

  registerOnChange(fn: any): void {
    console.log(`registerOnChange; fn: ${fn}`);
    this.form.valueChanges.subscribe((value) => {
      const phoneValue = value.phone ?? '';

      if (!phoneValue) {
        fn('');
      } else {
        const sanitizedPhone = `${value.country?.idd ?? ''}${phoneValue}`;
        fn(sanitizedPhone);
      }
    });
  }

  registerOnTouched(fn: any): void {
    console.log(`registerOnTouched; fn: ${fn}`);
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    console.log(`setDisabledState; isDisabled: ${isDisabled}`);
    if (isDisabled) {
      this.form.disable();
    } else {
      this.form.enable();
    }
  }

  validate(control: AbstractControl): ValidationErrors | null {
    if (this.form.valid) {
      return null;
    }

    return this.form.errors;
  }

  registerOnValidatorChange?(fn: () => void): void {
    console.log(`registerOnValidatorChange; fn: ${fn}`);
  }

  get inputClasses(): string[] {
    const classes: string[] = [];

    // if (this.enableCountry) {
    //   classes.push('border-s-0');
    // } else {
    //   classes.push('rounded-s-full');
    // }

    // if (this.submitLabel) {
    //   classes.push('border-e-0');
    // } else {
    //   classes.push('rounded-e-full');
    // }

    return classes;
  }

  onMenuClick(country: Country) {
    this.form.controls.country?.setValue(country);
    const dropdown = document.querySelector('#dropdown-phone');
    if (dropdown) {
      dropdown.classList.toggle('hidden');
    }
  }

  onSubmit() {
    const idd = this.formValues.country?.idd ?? '';
    const phone = this.formValues.phone ?? '';

    if (phone || !this.disabled) {
      const sanitizedPhone = `${idd}${phone}`;
      console.log(`sanitizedPhone: ${sanitizedPhone}`);
      this.formSubmitted.emit(sanitizedPhone);
    } else {
      this.form.updateValueAndValidity();
    }
  }

  async fetchCountryList(): Promise<void> {
    this.isCountryListLoading = true;
    var first: Nullable<Country>;

    try {
      this.countryList = await this.supportService.getCountries();

      if (this._countryList.length) {
        first = this._countryList[0];

        // if (first) {
        //   this.form.controls.country?.setValue(first);
        // }
      }
    } catch (error) {
      if (typeof error === 'string') {
        this.toastr.error(this.translate.instant(error));
      }
    } finally {
      this.isCountryListLoading = false;

      if (this.enableCountry && first) {
        this.form.addControl(
          'country',
          new FormControl(first, [
            Validators.required,
            // CommonValidators.include(this.countryList ?? []),
          ])
        );
      }
    }
  }
}
